import React, { useEffect, useMemo, useState } from "react";
import { Button, Chip, Input, Spinner, Switch } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";
import { AgentConfigInput } from "@webuild-ai/admin/models/components";
import { Tool } from "@webuild-ai/agent-platform/models/components";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useToolPreferencesStore } from "@/stores/ToolPreferencesStore";
import { useIntegrationOAuth } from "@/hooks/useIntegrationOAuth";
import { toolRequiresOAuth } from "@/config/integrations";
import { siteConfig } from "@/config/site";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";

interface ToolPickerProps {
  agentId: string;
  agent: AgentConfigInput;
  availableTools: Tool[];
  isDisabled: boolean;
}

type FilterTab = "all" | "enabled" | "available";

const getIntegrationTypeFromToolId = (toolId: string): string | null => {
  if (toolId.startsWith("mcp_")) {
    return toolId.slice(4); // strip "mcp_" prefix
  }

  return null;
};

export const ToolPicker: React.FC<ToolPickerProps> = ({
  agentId,
  agent,
  availableTools,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const { updateAgentTools } = useWorkflowStore();
  const { authenticatedFetch } = useWebuildAPIClient();
  const { ensureToolConnected, connectingToolId, prefetchStatuses, getToolConnectionStatus } =
    useIntegrationOAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const [disabledIntegrations, setDisabledIntegrations] = useState<Set<string>>(new Set());

  // Fetch tenant catalogue first, then pre-fetch statuses skipping disabled integrations
  useEffect(() => {
    if (!authenticatedFetch) return;

    const fetchCatalogThenStatuses = async () => {
      let disabled = new Set<string>();

      try {
        const baseUrl = siteConfig().adminAPIURL.replace(/\/$/, "");
        const response = await authenticatedFetch(
          `${baseUrl}/admin/v1/integrations/tenant/catalog`,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch tool catalogue: ${response.status}`);
        }

        const catalogue: { integration_type: string; enabled: boolean }[] = await response.json();

        disabled = new Set<string>(
          catalogue
            .filter((entry) => entry.enabled === false)
            .map((entry) => entry.integration_type),
        );
        setDisabledIntegrations(disabled);
      } catch {
        // On failure, default to empty set (show all tools)
        setDisabledIntegrations(new Set());
      }

      // Pre-fetch statuses only for enabled integrations
      prefetchStatuses(false, disabled);
    };

    fetchCatalogThenStatuses();
  }, [authenticatedFetch, prefetchStatuses]);

  // Resolve the current set of selected tool IDs
  const selectedToolIds: string[] = useMemo(() => {
    const paramTools = agent.parameters?.tools;

    return Array.isArray(paramTools) ? paramTools : [];
  }, [agent]);

  // Filter tools based on search + tab
  const filteredTools = useMemo(() => {
    let tools = availableTools;

    // Filter out tenant-disabled tools
    if (disabledIntegrations.size > 0) {
      tools = tools.filter((tool) => {
        const integrationType = getIntegrationTypeFromToolId(tool.id);

        return !integrationType || !disabledIntegrations.has(integrationType);
      });
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();

      tools = tools.filter(
        (tool) => tool.name.toLowerCase().includes(q) || tool.description.toLowerCase().includes(q),
      );
    }

    // Tab filter
    if (activeTab === "enabled") {
      tools = tools.filter((tool) => selectedToolIds.includes(tool.id));
    } else if (activeTab === "available") {
      tools = tools.filter((tool) => !selectedToolIds.includes(tool.id));
    }

    return tools;
  }, [availableTools, searchQuery, activeTab, selectedToolIds, disabledIntegrations]);

  const handleToggle = async (toolId: string, newValue: boolean) => {
    if (newValue) {
      const canEnable = await ensureToolConnected(toolId);

      if (canEnable) {
        updateAgentTools(agentId, toolId, true);
      } else {
        return;
      }
    } else {
      updateAgentTools(agentId, toolId, false);
    }

    // Persist to ToolPreferencesStore (localStorage)
    const workflow = useWorkflowStore.getState().workflow;

    if (workflow) {
      const currentTools = workflow.agents[agentId]?.parameters?.tools || [];

      useToolPreferencesStore.getState().setAgentTools(workflow.id, agentId, currentTools);
    }
  };

  return (
    <div className="flex flex-col gap-2" data-id="tools-section">
      {/* Search */}
      <div className="relative">
        <Icon
          className="w-4 h-4 text-default-400 absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none"
          icon="solar:magnifer-linear"
        />
        <Input
          aria-label={t("Search tools")}
          className="w-full pl-9"
          data-id="tools-search"
          placeholder={t("Search tools...")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Tabs */}
      <div
        aria-label={t("Filter tools")}
        className="flex bg-default-100 p-0.5 rounded-lg"
        data-id="tools-filter-tabs"
        role="tablist"
      >
        {(["all", "enabled", "available"] as FilterTab[]).map((tab) => (
          <button
            key={tab}
            aria-selected={activeTab === tab}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-default-400 ${
              activeTab === tab
                ? "bg-background text-foreground shadow-sm"
                : "text-default-500 hover:text-default-700"
            }`}
            role="tab"
            onClick={() => setActiveTab(tab)}
          >
            {t(tab.charAt(0).toUpperCase() + tab.slice(1))}
          </button>
        ))}
      </div>

      {/* Tool Row List */}
      <div
        className="flex flex-col rounded-lg border border-default-200 overflow-hidden max-h-[240px] overflow-y-auto"
        data-id="tools-list"
        role="list"
      >
        {filteredTools.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-default-400">
            {t("No tools found")}
          </div>
        ) : (
          filteredTools.map((tool) => {
            const isSelected = selectedToolIds.includes(tool.id);
            const isConnecting = connectingToolId === tool.id;
            const connectionStatus = getToolConnectionStatus(tool.id);
            const isOAuthTool = toolRequiresOAuth(tool.id);
            const isConnected = connectionStatus?.connected ?? false;
            const needsConnection = isOAuthTool && !isConnected;

            // Check for token expiry
            const tokenStatus = connectionStatus?.token_status;
            const isExpired =
              isConnected && (tokenStatus === "expired" || tokenStatus === "refresh_failed");
            const isExpiredOAuth = isOAuthTool && isExpired;
            const needsReconnect = isExpiredOAuth && isSelected;

            return (
              <div
                key={tool.id}
                className="group flex items-center gap-3 px-3 py-2.5 border-b border-default-100 last:border-b-0 hover:bg-default-50 transition-background"
                data-id={`tool-row-${tool.id}`}
                role="listitem"
              >
                {/* Icon */}
                <div className="w-7 h-7 flex items-center justify-center rounded-md bg-default-100 border border-default-200 shrink-0">
                  {tool.icon.startsWith("/") ? (
                    <img alt={tool.name} className="w-4 h-4 object-contain" src={tool.icon} />
                  ) : (
                    <Icon className="w-4 h-4" icon={tool.icon} />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{tool.name}</div>
                  <div className="text-xs text-default-400 truncate max-h-0 opacity-0 group-hover:max-h-5 group-hover:opacity-100 transition-[max-height,opacity] duration-150 motion-reduce:transition-none">
                    {tool.description}
                  </div>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-2 shrink-0">
                  {needsConnection && !isSelected && !isConnecting && (
                    <Chip
                      className="text-[10px] h-5"
                      data-id={`tool-status-${tool.id}`}
                      size="sm"
                      variant="secondary"
                    >
                      {t("Not connected")}
                    </Chip>
                  )}
                  {isExpiredOAuth && !isConnecting && (
                    <Chip
                      className="text-[10px] h-5"
                      data-id={`tool-expired-${tool.id}`}
                      size="sm"
                      variant="secondary"
                    >
                      {t(
                        tokenStatus === "refresh_failed"
                          ? "Reconnection Required"
                          : "Token Expired",
                      )}
                    </Chip>
                  )}
                  {needsReconnect && !isConnecting && (
                    <Button
                      className="text-[10px] h-6 min-w-[70px]"
                      data-id={`reconnect-${tool.id}`}
                      size="sm"
                      variant="secondary"
                      onPress={() => handleToggle(tool.id, true)}
                    >
                      <Icon icon="solar:danger-triangle-bold" /> {t("Reconnect")}
                    </Button>
                  )}
                  {isConnecting && <Spinner data-id={`tool-connecting-${tool.id}`} size="sm" />}
                  <Switch
                    aria-label={`${t("Toggle")} ${tool.name}`}
                    data-id={`tool-${tool.id}`}
                    isDisabled={isDisabled || isConnecting}
                    isSelected={isSelected}
                    size="sm"
                    onChange={(value) => handleToggle(tool.id, value)}
                  >
                    <Switch.Content>
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Content>
                  </Switch>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
